import { useDebounce } from '@mm/hooks';
import { useEntryCRUDActions } from '@mm/state/actions/EntryCRUDActions';
import AttributeModel from '@mm/state/model/AttributeModel';
import ButtonModel, {
  ButtonType,
  WebviewHeightRatio,
  WebviewOptions,
} from '@mm/state/model/ButtonModel';
import { FormEntry } from '@mm/state/model/FormsModel';
import { UploadImgModel } from '@mm/state/model/UploadImgModel';
import WidgetModel, {
  GalleryWidgetConfig,
  ListAndGalleryEntry,
  WidgetBase,
} from '@mm/state/model/WidgetModel';
import { InputMaxLength } from '@mm/components/src/InputMaxLength/InputMaxLength';
import React, { ChangeEvent, ReactText, useEffect, useState } from 'react';
import classnames from 'classnames';
import { isEqual } from 'lodash';
import DeleteCircleButton from '../../../../../../../../common/buttons/DeleteCircleButton/DeleteCircleButton';
import { getUpdatedTextWithAttribute } from '@mm/state/helpers/attribute-picker-helpers';
import AttributePicker from '../../../../../../../../common/components/AttributePicker/AttributePicker';
import { getUpdatedTextWithEmoji } from '../../../../../../../../common/components/EmojiPicker/emoji-picker-helpers';
import { EmojiPicker } from '../../../../../../../../common/components/EmojiPicker/EmojiPicker';
import ImageUpload from '../../../../../../../../common/components/ImageUpload/ImageUpload';
import PrimarySelect from '../../../../../../../../common/components/PrimarySelect/PrimarySelect';
import { useInputRef } from '../../../../../../../../common/widgets/common/use-input-ref';
import AttributeContainingTextInput from '../../../../../../../components/AttributeContainingTextInput/AttributeContainingTextInput';
import { ButtonListEditor } from '../../../ButtonListEditor/ButtonListEditor';
import './GalleryListItemBody.scss';

const MAX_FB_DESCRIPTION_LENGTH = 80;
const MAX_IG_DESCRIPTION_LENGTH = 500;
const MAX_FB_TITLE_LENGTH = 50;
const MAX_IG_TITLE_LENGTH = 250;

const webviewOptionsWrapped = WebviewOptions.map(option => ({
  ...option,
  name: `Webview Size - ${option.name}`,
}));

type Props = {
  attributes?: AttributeModel[];
  couldShowUploadFormModal?: boolean;
  errors: Record<string, any>;
  isValid: boolean;
  item: ListAndGalleryEntry;
  onCancelCrop: () => void;
  onDeleteClick: () => void;
  onItemUpdated: (
    item: Partial<FormEntry> | Partial<ListAndGalleryEntry>,
  ) => void;
  onOrderButtonClick: (forward?: boolean) => {};
  onPersist: () => void;
  onRatioChange: (isImageSquare?: boolean) => void;
  removeImage: () => void;
  showAttributePicker?: boolean;
  tooltipId: string;
  uploadImage: (image: UploadImgModel) => Promise<void>;
  widget: WidgetBase<GalleryWidgetConfig> & Partial<WidgetModel>;
  setUnsavedEntryButton?: (unsavedButton: ButtonModel | undefined) => void;
  disabledButtonTypes?: ButtonType[];
  usedInSlideout?: boolean;
  forInstagram?: boolean;
};

export const GalleryListItemBody = (props: Props) => {
  const {
    item,
    uploadImage,
    removeImage,
    onCancelCrop,
    onPersist,
    errors,
    attributes,
    widget,
    tooltipId,
    isValid,
    onDeleteClick,
    couldShowUploadFormModal,
    onRatioChange,
    onOrderButtonClick = (forward?: boolean) => {},
    setUnsavedEntryButton,
    disabledButtonTypes,
    showAttributePicker = true,
    usedInSlideout,
    forInstagram,
  } = props;

  const [title, setTitle] = useState(item.title);
  const [description, setDescription] = useState(item.description);
  const [url, setUrl] = useState(item.url);
  const [webviewHeightRatio, setWebviewHeightRatio] = useState(
    item.webviewHeightRatio,
  );

  const debouncedTitle = useDebounce<typeof title>(title, 500);
  const debouncedDescription = useDebounce<typeof description>(
    description,
    500,
  );
  const debouncedUrl = useDebounce<typeof url>(url, 500);
  const debouncedWebviewHeightRatio = useDebounce<typeof webviewHeightRatio>(
    webviewHeightRatio,
    500,
  );

  const styleType = widget.type;
  const maxLengthTitle = forInstagram
    ? MAX_IG_TITLE_LENGTH
    : MAX_FB_TITLE_LENGTH;
  const maxLengthDescription = forInstagram
    ? MAX_IG_DESCRIPTION_LENGTH
    : MAX_FB_DESCRIPTION_LENGTH;
  const { file: imgUrl } = item;
  const listHeaderClassName = styleType;
  const fixedRatioImage =
    widget.config.imageAspectRatio &&
    widget.config.imageAspectRatio === 'square';

  const {
    inputRef: descriptionInputRef,
    memoizedSetInputRef: setDescriptionInputRef,
  } = useInputRef(false);
  const { inputRef: titleInputRef, memoizedSetInputRef: setTitleInputRef } =
    useInputRef(false);
  const { inputRef: urlInputRef, memoizedSetInputRef: setUrlInputRef } =
    useInputRef(false);

  const { update: updateEntry } = useEntryCRUDActions();

  useEffect(() => {
    const newEntry = {
      ...item,
      description: debouncedDescription,
      url: debouncedUrl,
      webviewHeightRatio: debouncedWebviewHeightRatio,
    };

    if (!isEqual(item, newEntry)) {
      updateEntry(widget.id, newEntry);
    }
  }, [item, debouncedDescription, debouncedUrl, debouncedWebviewHeightRatio]);

  useEffect(() => {
    const newEntry = {
      ...item,
      title: debouncedTitle,
    };
    if (!isEqual(item.title, newEntry.title)) {
      updateEntry(widget.id, newEntry);
    }
  }, [debouncedTitle]);

  const onChange = (
    e: ChangeEvent<HTMLTextAreaElement> | any,
    field: 'title' | 'description' | 'url',
  ) => {
    const text = e.target.value;

    switch (field) {
      case 'title':
        setTitle(text);
        break;
      case 'description':
        setDescription(text);
        break;
      case 'url':
        setUrl(text);
        break;
    }
  };

  const onChangeWebview = ({ id }: { id: ReactText }) => {
    if (id) {
      setWebviewHeightRatio(id as WebviewHeightRatio);
    }
  };

  const updateInsertEmojiTitle = (emoji: string) => {
    const newText = getUpdatedTextWithEmoji(title, emoji, titleInputRef);
    setTitle(newText);
  };

  const updateInsertEmojiDescription = (emoji: string) => {
    const newText = getUpdatedTextWithEmoji(
      description,
      emoji,
      descriptionInputRef,
    );
    if (newText.length <= maxLengthDescription) {
      setDescription(newText);
    }
  };

  const updateInsertAttributeUrl = (attribute: string) => {
    const newText = getUpdatedTextWithAttribute(
      item.url,
      attribute,
      urlInputRef,
    );

    setUrl(newText);
  };

  const updateInsertAttributeTitle = (attribute: string) => {
    const newText = getUpdatedTextWithAttribute(
      title,
      attribute,
      titleInputRef,
    );
    setTitle(newText);
  };

  const updateInsertAttributeDescription = (attribute: string) => {
    const newText = getUpdatedTextWithAttribute(
      description,
      attribute,
      descriptionInputRef,
    );
    setDescription(newText);
  };

  const tooltipClassList = classnames(
    'fa',
    'fa-exclamation-circle',
    'fa-lg',
    'widget-item__error',
    {
      'widget-item__error--hidden': isValid,
    },
  );

  const errorsClassName = classnames(
    'widget-item__input gallery-list-item-body__widget-input-wrapper',
    'gallery-list-item-body__widget-input-wrapper-title',
    {
      'widget-item__attributes-error-container': errors.title || !title?.length,
    },
  );

  return (
    <div className="gallery-list-item-body__container">
      <div className="gallery-list-item-body__header">
        <h2 className="gallery-list-item-body__header-title">{title}</h2>
        <div className="gallery-list-item-body__header-button-area">
          <i id={tooltipId} className={tooltipClassList} />

          <div className="gallery-list-item-body__header-order-buttons">
            <div
              className="gallery-list-item-body__header-order-button"
              onClick={() => onOrderButtonClick()}
            >
              <div className="gallery-list-item-body__header-order-icon">
                <i className="fas fa-arrow-left" />
              </div>
            </div>
            Move
            <div className="gallery-list-item-body__header-order-button">
              <div
                className="gallery-list-item-body__header-order-icon"
                onClick={() => onOrderButtonClick(true)}
              >
                <i className="fas fa-arrow-right" />
              </div>
            </div>
          </div>

          <DeleteCircleButton onClick={onDeleteClick} />
        </div>
      </div>

      <div className="gallery-list-item-body__image-uploader">
        <ImageUpload
          title="Upload picture"
          onRatioChange={onRatioChange}
          imgUrl={imgUrl}
          uploadImage={uploadImage}
          removeImage={removeImage}
          onCancelCrop={onCancelCrop}
          fixedRatio={!!fixedRatioImage}
          couldBeRemoved
          couldShowUploadFormModal={couldShowUploadFormModal}
          shouldCroppedImmediately
          iconSize={2}
        />
      </div>
      <div className={`widget-item__inputs-container--${listHeaderClassName}`}>
        <div className={errorsClassName}>
          <AttributeContainingTextInput
            placeholder="Enter your title.."
            value={title}
            onChange={e => onChange(e, 'title')}
            onBlur={onPersist}
            attributes={attributes}
            multiline={false}
            inputRef={setTitleInputRef}
            maxLength={maxLengthTitle}
            enabledSuggestions={showAttributePicker}
          />
          <div className="widget-common__heading widget-common__icons-container gallery-list-item-body__icons-container">
            {showAttributePicker && (
              <AttributePicker
                classNameForIcon="gallery-list-item-body__attributes"
                onAttributePicked={updateInsertAttributeTitle}
                showOnlyNameAttributes={false}
                color="#9a9aa2"
              />
            )}
            <EmojiPicker
              className="gallery-list-item-body__emoji"
              onEmojiPicked={updateInsertEmojiTitle}
            />
          </div>
        </div>
        <div>
          <div className="widget-item__input gallery-list-item-body__widget-input-wrapper">
            <InputMaxLength
              max={maxLengthDescription}
              value={description}
              css={{ details: 'gallery-list-item-body__input-max-length' }}
              detailChildren={
                <div className="widget-common__heading widget-common__icons-container gallery-list-item-body__icons-container">
                  {showAttributePicker && (
                    <AttributePicker
                      classNameForIcon="gallery-list-item-body__attributes"
                      onAttributePicked={updateInsertAttributeDescription}
                      showOnlyNameAttributes={false}
                      color="#9b9b9b"
                    />
                  )}
                  <EmojiPicker
                    className="gallery-list-item-body__emoji"
                    onEmojiPicked={updateInsertEmojiDescription}
                    color="#9b9b9b"
                  />
                </div>
              }
            >
              <AttributeContainingTextInput
                placeholder="Enter your description..."
                className="gallery-list-item-body__description-widget"
                value={description}
                onChange={e => onChange(e, 'description')}
                onBlur={onPersist}
                attributes={attributes}
                inputRef={setDescriptionInputRef}
                multiline
                autosize={false}
                enabledSuggestions={showAttributePicker}
              />
            </InputMaxLength>
          </div>
        </div>
        <div className="gallery-list-item-body__url-and-webview-size">
          <div
            className={classnames(
              'widget-item__input',
              'gallery-list-item-body__widget-input-wrapper',
              'gallery-list-item-body__widget-input-wrapper-url',
              { 'widget-item__attributes-error-container': errors.url },
            )}
            title={errors.url && errors.url}
          >
            <AttributeContainingTextInput
              placeholder="http://website.com"
              value={url || ''}
              inputRef={setUrlInputRef}
              className={`widget-item__input--${listHeaderClassName}`}
              onChange={e => onChange(e, 'url')}
              onBlur={onPersist}
              attributes={attributes}
              multiline={false}
              enabledSuggestions={showAttributePicker}
            />
            <div className="widget-common__heading widget-common__icons-container gallery-list-item-body__icons-container">
              {showAttributePicker && (
                <AttributePicker
                  classNameForIcon="gallery-list-item-body__attributes"
                  onAttributePicked={updateInsertAttributeUrl}
                  showOnlyNameAttributes={false}
                  color="#9a9aa2"
                />
              )}
            </div>
          </div>
          <div className="gallery-list-item-body__widget-input-wrapper gallery-list-item-body__widget-input-wrapper-webview-size">
            <PrimarySelect
              showSearchFilter={false}
              placeholder="Select Webview Size"
              className="gallery-list-item-body__select-webview"
              onChange={webviewSize => onChangeWebview(webviewSize)}
              options={webviewOptionsWrapped}
              value={webviewOptionsWrapped.find(
                ({ id }) => id === webviewHeightRatio,
              )}
              customDropdownStyles={{
                zIndex: usedInSlideout ? '1000003' : '999',
              }}
            />
          </div>
        </div>
        <div
          className={`widget-item__button-container--${listHeaderClassName}`}
        >
          <ButtonListEditor
            buttonClass="gallery-list-item-body__button"
            useModal={false}
            widget={widget as WidgetModel}
            entry={item}
            buttons={item.buttons}
            maxButtons={3}
            useNewButton={true}
            setUnsavedEntryButton={setUnsavedEntryButton}
            disabledButtonTypes={disabledButtonTypes}
            usedInSlideout={usedInSlideout}
          />
        </div>
      </div>
    </div>
  );
};
